import type { Address } from "viem"
import { format, fromUnixTime } from "date-fns"
import { computed, makeObservable, observable, runInAction } from "mobx"

import type { OrchestratedQueryStore } from "@/domains/library"
import type { IOrchestratedQuerySender } from "@/domains/library/orchestrated-query-interfaces"
import type { LockRewardRepository, LockRewardsOnChainTransport, LockRewardStoreDAO } from "@/domains/lock-rewards"
import type { ITokenLookup } from "@/domains/tokens/types"
import { LcAddress } from "@/domains/core"
import { OrchestratedQuery } from "@/domains/library"
import { convertRawBribeDataToDAO, LockRewardStore } from "@/domains/lock-rewards"

import type { ILockRefreshStrategy } from "./interfaces"
import type { ILock, LastEpochVote, LockStoreDAO } from "./types"

export class LockStore implements ILock {
  // Core NFT data
  id: number
  lockAmount: number
  lockAmountRaw: bigint // Raw BigInt from RPC for exact precision
  lockEnds: number
  lockValue: number
  // lockPower: number
  // lockDuration: number
  // lockWeeks: number
  isMaxLocked: boolean
  hasExpired: boolean
  isReset: boolean
  hasVotes: boolean
  hasVotedThisEpoch: boolean
  lastVoted: number
  needsResetToWithdraw: boolean
  canWithdraw: boolean

  // Last epoch votes (votes cast in the previous epoch)
  lastEpochVotes: Array<LastEpochVote> = []

  // Rewards polling (optional - only created if enableRewards is true)
  private rewardsQuery: OrchestratedQuery<LockRewardStoreDAO[]> | undefined
  private lockRewardsOnChainTransport: LockRewardsOnChainTransport
  private lockRewardRepository: LockRewardRepository
  private tokenLookup: ITokenLookup

  // Lock refresh strategy (optional, for force refresh)
  private lockRefreshStrategy: ILockRefreshStrategy | null | undefined

  constructor(
    data: LockStoreDAO,
    lockRewardsOnChainTransport: LockRewardsOnChainTransport,
    lockRewardRepository: LockRewardRepository,
    tokenLookup: ITokenLookup,
    orchestratedQueryStore: OrchestratedQueryStore,
    lockRefreshStrategy: ILockRefreshStrategy | null | undefined,
    queryNamePrefix?: string,
    enableRewards: boolean = true, // Default to true for backward compatibility
  ) {
    // Initialize all fields from DAO
    this.id = data.id
    this.lockAmount = data.lockAmount
    this.lockAmountRaw = data.lockAmountRaw
    this.lockEnds = data.lockEnds
    this.lockValue = data.lockValue
    this.isMaxLocked = data.isMaxLocked
    this.hasExpired = data.hasExpired
    this.isReset = data.isReset
    this.hasVotes = data.hasVotes
    this.hasVotedThisEpoch = data.hasVotedThisEpoch
    this.lastVoted = data.lastVoted
    this.needsResetToWithdraw = data.needsResetToWithdraw
    this.canWithdraw = data.canWithdraw

    // Store rewards dependencies (required)
    this.lockRewardsOnChainTransport = lockRewardsOnChainTransport
    this.lockRewardRepository = lockRewardRepository
    this.tokenLookup = tokenLookup

    // Store refresh strategy
    this.lockRefreshStrategy = lockRefreshStrategy

    // Only create and start rewards query if enabled
    // Managers can only vote, not claim rewards, so we skip rewards syncing for manager locks
    if (enableRewards) {
      this.rewardsQuery = new OrchestratedQuery<LockRewardStoreDAO[]>(
        `${queryNamePrefix ? queryNamePrefix + " " : ""}Lock #${this.id} Rewards Sync`,
        (_query) => this.loadRewards(), // Query parameter available but not used (no chunking)
        60_000, // 60 seconds
        this.onLoadRewardsSuccess.bind(this),
        this.onLoadRewardsFailed.bind(this),
        orchestratedQueryStore,
      )

      // Start polling immediately
      this.rewardsQuery.start()
    }

    makeObservable(this, {
      // Observable properties
      id: observable,
      lockAmount: observable,
      lockAmountRaw: observable,
      lockEnds: observable,
      lockValue: observable,

      // lockPower: observable,
      // lockDuration: observable,
      // lockWeeks: observable,
      isMaxLocked: observable,
      hasExpired: observable,
      isReset: observable,
      hasVotes: observable,
      hasVotedThisEpoch: observable,
      lastVoted: observable,
      needsResetToWithdraw: observable,
      canWithdraw: observable,
      lastEpochVotes: observable,

      // Computed properties
      canTransfer: computed,
      displayLockAmount: computed,
      displayLockEnds: computed,
      displayLockPower: computed,
      displayLockValue: computed,
      isScalar: computed,
      isScalest: computed,
      power: computed,
      totalRewardsValueUSD: computed,
      hasRewardsToClaim: computed,
    })
  }

  // Computed properties for display formatting
  get displayLockAmount(): string {
    return this.lockAmount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }

  get displayLockValue(): string {
    return `$${this.lockValue.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
  }

  get displayLockPower(): string {
    // return this.lockPower.toLocaleString("en-US", {
    //   minimumFractionDigits: 4,
    //   maximumFractionDigits: 4,
    // })
    return "0"
  }

  get displayLockEnds(): string {
    return format(fromUnixTime(this.lockEnds), "MMM do, yyyy")
  }

  get canTransfer() {
    return !this.hasVotedThisEpoch
  }

  get power() {
    return this.lockAmount > 0 ? Number(this.lockValue) / Number(this.lockAmount) : 0
  }

  get isScalest(): boolean {
    return this.lockValue > 5000
  }

  get isScalar(): boolean {
    return this.lockValue <= 5000
  }

  calculateInfluence(veTotalSupply: number): number {
    return this.lockAmount / veTotalSupply
  }

  /**
   * Update last epoch votes for this lock
   * Converts the transport format to LastEpochVote format
   */
  updateLastEpochVotes(votes: Array<{ address: Address; balance: number; votePercent: number }>): void {
    runInAction(() => {
      this.lastEpochVotes = votes.map((vote) => ({
        gaugeAddress: new LcAddress(vote.address),
        votePercent: vote.votePercent,
        balance: vote.balance,
      }))
    })
  }

  /**
   * Get last epoch vote for a specific gauge
   */
  getLastEpochVoteForGauge(gaugeAddress: LcAddress): LastEpochVote | undefined {
    return this.lastEpochVotes.find((vote) => vote.gaugeAddress.equals(gaugeAddress))
  }

  // Update static data (useful for refreshing from API)
  updateStaticData(data: Partial<LockStoreDAO>): void {
    // Update fields that can change
    if (data.canWithdraw !== undefined) this.canWithdraw = data.canWithdraw
    if (data.hasExpired !== undefined) this.hasExpired = data.hasExpired
    if (data.hasVotedThisEpoch !== undefined) this.hasVotedThisEpoch = data.hasVotedThisEpoch
    if (data.hasVotes !== undefined) this.hasVotes = data.hasVotes
    if (data.isMaxLocked !== undefined) this.isMaxLocked = data.isMaxLocked
    if (data.isReset !== undefined) this.isReset = data.isReset
    if (data.lastVoted !== undefined) this.lastVoted = data.lastVoted
    if (data.lockAmount !== undefined) this.lockAmount = data.lockAmount
    if (data.lockAmountRaw !== undefined) this.lockAmountRaw = data.lockAmountRaw
    if (data.lockEnds !== undefined) this.lockEnds = data.lockEnds
    if (data.lockValue !== undefined) this.lockValue = data.lockValue
    if (data.needsResetToWithdraw !== undefined) this.needsResetToWithdraw = data.needsResetToWithdraw
  }

  // ===== REWARDS MANAGEMENT =====

  /**
   * Load rewards for this NFT from on-chain
   * Triggered by OrchestratedQuery
   */
  private async loadRewards(): Promise<Array<LockRewardStoreDAO>> {
    const rawResults = await this.lockRewardsOnChainTransport.loadRewardsByNftId(this.id)
    const results: Array<LockRewardStoreDAO> = []

    for (const raw of rawResults) {
      try {
        const [_pair, __bribe, tokenAddress] = raw.A

        const token = this.tokenLookup.find(new LcAddress(tokenAddress))

        if (!token) {
          console.error(`❌ Token not found for reward: ${tokenAddress} (NFT: ${this.id}), skipping`)
          continue
        }

        const rewardDAO = convertRawBribeDataToDAO(this.id, raw, token)
        results.push(rewardDAO)
      } catch (error) {
        console.error(`Error processing reward for NFT ${this.id}:`, raw, error)
      }
    }

    return results
  }

  /**
   * Process successful rewards load
   */
  private onLoadRewardsSuccess(
    _sender: IOrchestratedQuerySender<LockRewardStoreDAO[]>,
    data: LockRewardStoreDAO[],
  ): void {
    try {
      runInAction(() => {
        const processedKeys = new Set<string>()
        const existingRewards = this.lockRewardRepository.getByNftId(this.id)

        // Process new/updated rewards
        for (const rewardDAO of data) {
          try {
            const rewardStore = new LockRewardStore(rewardDAO)
            processedKeys.add(rewardDAO.key)

            this.lockRewardRepository.upsert(rewardStore)
          } catch (error) {
            console.error(`Error processing reward for NFT ${this.id}:`, rewardDAO, error)
          }
        }

        // Remove claimed rewards (exist in repo but not in new data)
        for (const existingReward of existingRewards) {
          if (!processedKeys.has(existingReward.key)) {
            this.lockRewardRepository.remove(existingReward.key)
          }
        }
      })
    } catch (error) {
      console.error(`❌ Error processing rewards for Lock #${this.id}:`, error)
    }
  }

  private onLoadRewardsFailed(_sender: IOrchestratedQuerySender<LockRewardStoreDAO[]>, error: Error): void {
    console.error(`❌ Failed to load rewards for Lock #${this.id}:`, error)
  }

  get rewards(): LockRewardStore[] {
    return this.lockRewardRepository.getByNftId(this.id)
  }

  getRewards(): LockRewardStore[] {
    return this.rewards
  }

  refreshRewards(): void {
    if (this.rewardsQuery) {
      this.rewardsQuery.forceExecute()
    }
  }

  /**
   * Force refresh this lock's data from chain
   * This triggers a refresh of all locks, then updates this lock's state
   */
  async forceRefresh(): Promise<void> {
    console.log("calling LockStore.forceRefresh", this.id)
    if (!this.lockRefreshStrategy) {
      console.warn("LockStore.forceRefresh: No refresh strategy available for lock", this.id)
      return
    }
    await this.lockRefreshStrategy.refresh()
  }

  get totalRewardsValueUSD(): number {
    return this.lockRewardRepository.getByNftId(this.id).reduce((total, reward) => total + reward.valueUSD, 0)
  }

  get hasRewardsToClaim(): boolean {
    return this.rewards.some((reward) => reward.amount > 0)
  }

  dispose(): void {
    if (this.rewardsQuery) {
      this.rewardsQuery.dispose()
    }
    this.lockRewardRepository.clearForNftId(this.id)
  }
}
